<script setup lang="ts">
import type { PreferredLanguage } from '~/models/User/PreferredLanguage'
import type { AudioPlayerVariant } from '~/models/Media'
import type { TTSItem, TTSLanguage } from '~/models/Brikke/TextToSpeech'
import type { ButtonSizes } from '~/components/ui/Button.vue'
import { useI18n } from 'vue-i18n'
import { computed, nextTick, ref, useSlots, useTemplateRef, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useWindowSize } from '@vueuse/core'
import { generateTtsWithRetry } from '~/utils/ttsUtils'
import { AuToast } from '~/stores/toasts'
import useAuthStore from '~/stores/auth'
import { PendoFeature } from '~/models/Pendo'
import { useTextToSpeech } from '~/composables/useTextToSpeech'
import useLanguageMap from '~/composables/useLanguageMap'
import { useFeatureFlag } from '~/composables/useFeatureFlag'
import { getAccessToken } from '~/api/client/oidcClient'
import { useBrikkeApi } from '~/api/brikkeApi'
import AudioPlayer from '~/components/media/AudioPlayer.vue'

const {
  item,
  languageCode,
  variant = 'button-with-text',
  selector = '.au-article',
  hideButtonWhenOpen = false
} = defineProps<{
  item: TTSItem
  languageCode: TTSLanguage | null
  variant?: AudioPlayerVariant
  selector?: string
  hideButtonWhenOpen?: boolean
  size?: ButtonSizes
}>()

const src = ref('')
const retries = ref(0)
const isLoading = ref(false)
const isPlaying = ref(false)
const playerEl = useTemplateRef('player')

const { t } = useI18n()
const { textToSpeech } = useBrikkeApi()
const { isAuthenticated, userPreferredLanguage } = storeToRefs(useAuthStore())
const { findText, mapLanguageToVoice, getCacheKeys } = useTextToSpeech()
const { languageMap, defaultLanguage } = useLanguageMap()
const { isFeatureEnabled } = useFeatureFlag()
const { width } = useWindowSize()
const slots = useSlots()

const loadingText = computed(() => retries.value > 1
  ? t('media.retryingAudio')
  : t('media.loadingAudio'))

const itemLanguage = computed(() => item.languageCodes.includes(languageMap[userPreferredLanguage.value])
  ? userPreferredLanguage.value
  : defaultLanguage)

const isoLanguage = computed((): TTSLanguage => {
  if (languageCode && !['nb_NO', 'nn_NO'].includes(languageCode)) {
    return languageCode
  }
  const norMap: Record<PreferredLanguage, TTSLanguage> = {
    nb: 'nb_NO',
    nn: 'nn_NO',
  }
  return norMap[itemLanguage.value]
})

const voice = computed(() => mapLanguageToVoice(isoLanguage.value))
const label = computed(() => width.value < 768 ? t('media.audioShort') : t('media.audioArticle'))

const key = computed(() => {
  const env = import.meta.env.VITE_ENV
  let key = `${item.locationId}-${item.versionNo}-${isoLanguage.value}-${voice.value}`
  if (env && env !== 'prod')
    key += `-${env}`
  return key
})

const tracking = computed(() => ({
  'data-pendo': isPlaying.value
    ? PendoFeature.TextToSpeech.PauseButton
    : PendoFeature.TextToSpeech.PlayButton
}))

watch(
  () => item,
  async () => {
    await playerEl.value?.close()
    setTimeout(() => src.value = '', 0)
  },
)

const generate = async () => {
  await nextTick()
  const text = findText(selector)
  if (!text.trim()) {
    AuToast.error(t('tts.missingText'))
    return
  }

  const keys = getCacheKeys(selector)
  isLoading.value = true
  retries.value = 0

  const result = await generateTtsWithRetry(
    textToSpeech,
    {
      key: keys.length > 0 ? `${key.value}-${keys}` : key.value,
      title: item.title === text ? `${text}.` : item.title, // NOTE: BW bug - title and text can not be the same, thats why we add the `${text}.` to the title
      title_enabled: false,
      skip_content_check: true,
      content: text,
      voice: voice.value,
      platform: 'aunivers',
      authToken: getAccessToken(),
      source_url: window.location.href,
      language: isoLanguage.value,
    },
    (attempt) => {
      retries.value = attempt
    },
  )

  isLoading.value = false

  if (result.error) {
    AuToast.error(t('tts.generateError'))
    console.error(result.error)
    return
  }

  if (result.url) {
    src.value = result.url
    retries.value = 0
  }
}
</script>

<template>
  <AudioPlayer
    v-if="isAuthenticated && isFeatureEnabled('TTS')"
    ref="player"
    :variant
    load="visible"
    autoplay
    :ai-generated="true"
    :src
    :data-pendo="tracking['data-pendo']"
    :hide-button-when-open
    :size
    @open="generate"
    @play="isPlaying = true"
    @pause="isPlaying = false"
  >
    <template v-if="isLoading" #default>
      <div class="flex-grow text-center text-xl font-bold">
        {{ loadingText }}
      </div>
    </template>

    <template #button-text>
      {{ label }}
    </template>

    <template v-if="slots.trigger" #trigger="{ activate, play, pause, isPlaying, isActive }">
      <slot
        name="trigger"
        :play
        :activate
        :pause
        :is-playing
        :is-active
        :generate
        :is-loading-tts="isLoading"
      />
    </template>
  </AudioPlayer>
</template>
